import {
  createTraining,
  fetchTimeBlocks,
  getCoaches,
  getSubscriptions,
  registerTransaction,
  sendReservations,
} from '../../api';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { IPlace } from '@/components/Dto';
import { useHookState } from '../../NotificationApiHook';
import {
  Autocomplete,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  Skeleton,
  Container,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { AdminBalanceContainer } from '../AdminBalance/AdminBalance.styled';
import {
  CoachResponseDTO,
  CreateSubscriptionRequestDto,
  SubscriptionResponseDTO,
} from '../../../api/DTO';
import { TimeRange } from '../AdminBookings/utils';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { format } from 'date-fns';
import {
  AdminTrainingFormData,
  DAYS_OPTIONS,
  getTablesByPlace,
  getTimeOptions,
  TRAIN_AHEAD_OPTIONS,
  TransactionStatus,
} from './utils';
import {
  getReservationsForYear,
  SelectedTables,
  getDaysForYear,
} from '../../ReservationModal/utils';
import LoadingButton from '@mui/lab/LoadingButton';
import { useNotification } from '../../NotificationContext';

export interface AdminTrainingsProps {
  placesOptions: IPlace[];
}

interface AutocompleteOption {
  id: number;
  value: number;
  label: string;
  user_id: number;
}

const AdminTrainings = ({ placesOptions }: AdminTrainingsProps) => {
  const methods = useForm();
  const { handleSubmit, register, control, watch, reset, setValue } = methods;
  const { openNotification } = useNotification();

  const { notificationState } = useHookState();
  // Мапа временных интервалов (ключ - placeId-date, значение - мапа временных интервалов)
  const [timeRangesMap, setTimeRangesMap] = useState<Map<string, TimeRange[]>>(new Map());

  const [isLoadingTrainings, setIsLoadingTrainings] = useState(false);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);

  const [coachOptions, setCoachOptions] = useState<AutocompleteOption[]>([]);
  const [subscriptonOptions, setSubscriptonOptions] = useState([]);
  const [generateDaysQty, setGenerateDaysQty] = useState(0);
  const [dayAhead, setDayAhead] = useState(new Date());

  const daysOptions = useMemo(() => DAYS_OPTIONS, []);

  const isAdmin = localStorage.getItem('role_id') === '1';

  useEffect(() => {
    const cityId = localStorage.getItem('city_id') ?? '1';
    getCoaches(notificationState, cityId).then((data) => {
      setCoachOptions(
        data?.map((coach: CoachResponseDTO) => ({
          ...coach,
          value: coach.id,
          label: coach.coach_name,
        })),
      );
    });
  }, []);

  const place_id = watch('place_id');
  const date = watch('day');
  const start_time_id = watch('start_time_id');
  const withTraining = watch('withTrainings');
  const withBookings = watch('withBookings');

  useEffect(() => {
    if (!place_id) return; // Если place_id еще не выбран, не загружать тренеров
    const place = placesOptions.find((p) => p.id === place_id); // Ищем выбранный зал
    const cityId = place?.city_id ?? '1'; // Если cityId не найден в place, берем '1' как дефолт

    getCoaches(notificationState, cityId).then((data) => {
      setCoachOptions(
        data?.map((coach: CoachResponseDTO) => ({
          ...coach,
          value: coach.id,
          label: coach.coach_name,
        })),
      );
    });
  }, [place_id, placesOptions]);

  useEffect(() => {
    if (!place_id) return;

    getSubscriptions(notificationState, place_id).then((data: SubscriptionResponseDTO[]) => {
      const uniqueSubscriptions = data.reduce(
        (acc: SubscriptionResponseDTO[], sub: SubscriptionResponseDTO) => {
          if (!acc.find((item) => item.id === sub.id)) {
            acc.push(sub);
          }
          return acc;
        },
        [],
      );

      const subscriptionOptions = uniqueSubscriptions.map((sub) => ({
        ...sub,
        value: sub.id,
        label: sub.subscription_name,
      }));

      setSubscriptonOptions(subscriptionOptions);
    });
  }, [place_id]);

  //   Сброс чекбоксов выбора дня недели при смене даты
  useEffect(() => {
    if (!date) return;
    daysOptions.forEach((day) => {
      setValue(`days.${day.value}`, false);
    });
    setValue(`days.День-${date.getDay()}`, true);
    setDayAhead(new Date(date.getTime() + generateDaysQty * 86400000)); // cast to milliseconds
  }, [date]);

  useEffect(() => {
    if (date && place_id) {
      fetchTimeRanges();
    }
    setValue('tables', null);
  }, [date, place_id]);

  const handleChipClick = (daysQty: number) => {
    setGenerateDaysQty(daysQty);
    setDayAhead(new Date(date.getTime() + daysQty * 86400000)); // cast to milliseconds
  };

  const fetchTimeRanges = async () => {
    const formatedDate = date.toLocaleDateString('ru-RU');
    if (timeRangesMap.get(`${place_id}-${formatedDate}`)) {
      return timeRangesMap.get(`${place_id}-${formatedDate}`);
    }

    const timeBlocksFromServer = await fetchTimeBlocks(date, place_id, notificationState);

    // @ts-ignore
    timeRangesMap.set(`${place_id}-${formatedDate}`, timeBlocksFromServer);
    setTimeRangesMap(new Map(timeRangesMap));
  };

  const today = new Date();
  const onSubmit = (data: AdminTrainingFormData) => {
    const formValues: CreateSubscriptionRequestDto = {
      ...data,
      day: format(data.day, 'yyyy-MM-dd'),
    };

    const selectedDaysNumbers = [];
    for (const day in data.days) {
      const value = data.days[day];
      const dayNumber = parseInt(day.split('-')[1]);
      if (day.split('-')[1] !== 'NaN' && value) {
        selectedDaysNumbers.push(dayNumber);
      }
    }

    const selectedTables: number[] = [];

    for (const table in data.tables) {
      const value = data.tables[table];
      const tableNumber = parseInt(table.split('-')[1]);
      if (table.split('-')[1] !== 'NaN' && value) {
        selectedTables.push(tableNumber);
      }
    }

    const startDate = data.day;
    const checkboxSelectedDates = [];

    // Проверяем следующие 7 дней
    for (let i = 0; i <= 6; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);

      // Получаем день недели для текущей даты
      const dayNumber = currentDate.getDay();

      // Проверяем, соответствует ли день недели выбранным дням
      if (selectedDaysNumbers.includes(dayNumber)) {
        checkboxSelectedDates.push({
          formattedDate: format(currentDate, 'yyyy-MM-dd'),
          date: currentDate,
        });
      }
    }

    if (!data.coach_id) {
      openNotification('Не выбран владелец бронирования', 'error');
      return;
    }

    if (!selectedDaysNumbers.length) {
      openNotification('Не выбрано ни одного дня', 'error');
      return;
    }

    if (!data.start_time_id || !data.end_time_id) {
      openNotification('Не выбрано время', 'error');
      return;
    }

    if (withTraining && (!data.subscription_id || !data.quantity)) {
      openNotification('Не выбраны параметры тренировки', 'error');
      return;
    }

    if (withTraining && !data.info) {
      openNotification('Не указано название тренировки', 'error');
      return;
    }

    if (withBookings && !selectedTables.length) {
      openNotification('Не выбрано ни одного стола', 'error');
      return;
    }
    const withDayAhead = generateDaysQty > 0;
    const coach = coachOptions.find((coach) => coach.value === data.coach_id);
    const allReservations: any = [];
    console.log(checkboxSelectedDates);
    console.log(withDayAhead);
    checkboxSelectedDates.forEach((day) => {
      // Для каждого timeRangeId
      for (let i = data.start_time_id; i <= data.end_time_id; i++) {
        // Для каждого id стола
        for (let j = 0; j < selectedTables.length; j++) {
          const timeIdTableId: SelectedTables = { [i]: selectedTables[j] };
          const bookUntil = withDayAhead ? dayAhead : day.date;
          const reservationsAhead = getReservationsForYear(
            day.date,
            bookUntil,
            timeIdTableId,
            coach.user_id,
            place_id,
          );
          allReservations.push(...reservationsAhead);
        }
      }
    });

    const allDays: { day: string }[] = [];
    checkboxSelectedDates.forEach((day) => {
      const bookUntil = withDayAhead ? dayAhead : day.date;
      console.log('DAY AND UNTIL', day, bookUntil);
      const trainingsAhead = getDaysForYear(day.date, bookUntil);
      allDays.push(...trainingsAhead);
    });
    console.log('DAys', allDays);
    const allTrainings = allDays.map((day) => ({
      ...day,
      coach_id: coach.id,
      info: data.info,
      place_id: place_id,
      quantity: data.quantity,
      start_time_id: data.start_time_id,
      end_time_id: data.end_time_id,
      subscription_id: data.subscription_id,
    }));
    console.log('formValues', formValues);
    console.log('allReservations:', allReservations);
    console.log('allTrainings:', allTrainings);

    if (withTraining) {
      setIsLoadingTrainings(true);
      createTraining(notificationState, allTrainings)
        .then((data) => {
          notificationState.openNotification(`Тренировка создана успешно`, `success`);
          reset();
        })
        // Ошибка ловится на уровне апи, но это нужно оставить
        .catch((error) => {})
        .finally(() => setIsLoadingTrainings(false));
    }

    if (withBookings) {
      setIsLoadingBookings(true);

      const token = localStorage.getItem('token');

      registerTransaction(allReservations, token, notificationState, 'book')
        .then((data) => {
          const { id: paymentId, order_id: orderId, total_price: totalPrice } = data;
          resolveTransaction({
            order_id: orderId,
            payment_id: paymentId,
            total_price: totalPrice,
            type: 'book',
          });
        })
        // Ошибка ловится на уровне апи, но это нужно оставить
        .catch((error) => {})
        .finally(() => setIsLoadingBookings(false));
    }
  };

  const resolveTransaction = async (status: TransactionStatus) => {
    const token = localStorage.getItem('token');

    if (status.total_price === 0) {
      openNotification(`Ваша бронь подтверждена и оплачена с баланса вашего профиля`, 'success');
    } else {
      const isSuccess = await sendReservations(token, status, notificationState);
      if (!isSuccess) {
        console.log('Failed to send reservations.');
      }
    }
  };

  const tablesByPlace = useMemo(
    () => getTablesByPlace(date, place_id, timeRangesMap),
    [date, place_id, timeRangesMap],
  );

  const timeOptions = useMemo(
    () => getTimeOptions(date, place_id, timeRangesMap),
    [date, place_id, timeRangesMap],
  );

  if (!isAdmin) {
    return (
      <Container sx={{ marginTop: '100px' }}>
        <Skeleton height={60} />
        <Skeleton height={60} />
        <Skeleton height={60} />
        <Skeleton height={60} />
        <Skeleton height={60} />
      </Container>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <AdminBalanceContainer>
          <Stack
            direction='row'
            spacing={1}
          >
            <FormControlLabel
              control={
                <Controller
                  name={`withTrainings`}
                  control={control}
                  defaultValue={true}
                  render={({ field: { value, ref, ...field } }) => (
                    <Checkbox
                      {...field}
                      inputRef={ref}
                      checked={!!value}
                      color='primary'
                      size={'medium'}
                      disableRipple
                    />
                  )}
                />
              }
              label={'Создать тренировки'}
              labelPlacement='end'
            />
            <FormControlLabel
              control={
                <Controller
                  name={`withBookings`}
                  control={control}
                  defaultValue={true}
                  render={({ field: { value, ref, ...field } }) => (
                    <Checkbox
                      {...field}
                      inputRef={ref}
                      checked={!!value}
                      color='primary'
                      size={'medium'}
                      disableRipple
                    />
                  )}
                />
              }
              label={'Создать бронирования'}
              labelPlacement='end'
            />
          </Stack>
          <Controller
            name='place_id'
            control={control}
            render={({ field, fieldState }) => (
              <Autocomplete
                {...field}
                disableClearable
                options={placesOptions}
                onChange={(event, newValue) => {
                  field.onChange(newValue?.value);
                }}
                sx={{ width: '270px' }}
                renderInput={(params) => (
                  <TextField
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    {...params}
                    label={'Зал'}
                  />
                )}
              />
            )}
          />

          <Controller
            name='day'
            control={control}
            render={({ field, fieldState }) => (
              <DatePicker
                {...field}
                label={'Стартовая дата'}
                minDate={today}
                sx={{ width: '270px' }}
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState?.error?.message,
                  },
                }}
              />
            )}
          />

          {date && place_id ? (
            <>
              <Controller
                name='coach_id'
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    {...field}
                    disableClearable
                    options={coachOptions}
                    onChange={(event: any, newValue: any) => {
                      field?.onChange(newValue?.value);
                    }}
                    sx={{ width: '270px' }}
                    renderInput={(params) => (
                      <TextField
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        {...params}
                        label='Тренер'
                      />
                    )}
                  />
                )}
              />

              <Typography
                variant='h6'
                sx={{ textAlign: 'center' }}
              >
                Выбор дней недели
              </Typography>

              <Stack
                direction='row'
                spacing={1}
                key={date}
                sx={{ flexWrap: 'wrap' }}
              >
                {daysOptions.map((day) => (
                  <div key={`${date}-day.value`}>
                    <FormControlLabel
                      control={
                        <Controller
                          name={`days.${day.value}`}
                          control={control}
                          defaultValue={false}
                          render={({ field: { value, ref, ...field } }) => (
                            <Checkbox
                              {...field}
                              inputRef={ref}
                              checked={!!value}
                              color='primary'
                              size={'medium'}
                              disableRipple
                            />
                          )}
                        />
                      }
                      label={day.label}
                      labelPlacement='end'
                    />
                  </div>
                ))}
              </Stack>

              <Typography
                variant='h6'
                sx={{ textAlign: 'center' }}
              >
                Выбор времени
              </Typography>

              {!!timeOptions.start?.length && (
                <Controller
                  name='start_time_id'
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      {...field}
                      disableClearable
                      options={timeOptions.start}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      onChange={(event: any, newValue: any) => {
                        field?.onChange(newValue?.value);
                      }}
                      sx={{ width: '270px' }}
                      renderInput={(params) => (
                        <TextField
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          {...params}
                          label={'Время начала'}
                        />
                      )}
                    />
                  )}
                />
              )}

              {!!timeOptions.end?.length && (
                <Controller
                  name='end_time_id'
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      {...field}
                      disableClearable
                      options={timeOptions.end}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      onChange={(event: any, newValue: any) => {
                        field?.onChange(newValue?.value);
                      }}
                      getOptionDisabled={(option) => option.value < start_time_id}
                      sx={{ width: '270px' }}
                      renderInput={(params) => (
                        <TextField
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          {...params}
                          label={'Время окончания'}
                        />
                      )}
                    />
                  )}
                />
              )}

              {withTraining && (
                <>
                  <Typography
                    variant='h6'
                    sx={{ textAlign: 'center' }}
                  >
                    Параметры тренировки
                  </Typography>
                  <Controller
                    name='info'
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        {...field}
                        label='Название'
                        sx={{ width: '270px' }}
                      />
                    )}
                  />
                  <Controller
                    name='quantity'
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        {...field}
                        onChange={(e) =>
                          field.onChange(
                            isNaN(parseInt(e.target.value)) ? '' : parseInt(e.target.value),
                          )
                        }
                        label='Кол-во игроков'
                        sx={{ width: '270px' }}
                        type='number'
                      />
                    )}
                  />

                  <Controller
                    name='subscription_id'
                    control={control}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        {...field}
                        disableClearable
                        options={subscriptonOptions}
                        onChange={(event: any, newValue: any) => {
                          field?.onChange(newValue?.value);
                        }}
                        sx={{ width: '270px' }}
                        renderInput={(params) => (
                          <TextField
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            {...params}
                            label='Абонемент'
                          />
                        )}
                      />
                    )}
                  />
                </>
              )}

              {withBookings && (
                <>
                  <Typography
                    variant='h6'
                    sx={{ textAlign: 'center' }}
                  >
                    Выбор столов
                  </Typography>

                  <Stack
                    direction='row'
                    spacing={1}
                  >
                    {tablesByPlace?.map((table) => {
                      return (
                        <div key={`${table.value}-${place_id}`}>
                          <FormControlLabel
                            control={<Checkbox {...register(`tables.Стол-${table.value}`)} />} //Стол - id стола
                            label={`Стол ${table.label}`}
                          />
                        </div>
                      );
                    })}
                  </Stack>
                </>
              )}

              <Divider sx={{ my: 1, width: '100%' }} />
            </>
          ) : (
            <Typography
              variant='subtitle2'
              color='error'
            >
              Выберите зал и дату
            </Typography>
          )}
          {(withTraining || withBookings) && (
            <>
              <Typography variant='h6'>Бронь вперед</Typography>
              <Stack
                direction='row'
                spacing={1}
              >
                {TRAIN_AHEAD_OPTIONS?.map(({ label, value }) => (
                  <Chip
                    label={label}
                    variant={generateDaysQty === value ? 'filled' : 'outlined'}
                    onClick={() => handleChipClick(value)}
                    color='primary'
                  />
                ))}
              </Stack>
              {generateDaysQty !== 0 && (
                <Typography sx={{ fontSize: '0.9rem', textAlign: 'center', width: '100%' }}>
                  {`Забронируем до: ${dayAhead.toLocaleDateString('ru-RU', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                `}
                </Typography>
              )}
            </>
          )}
          <LoadingButton
            loading={isLoadingTrainings || isLoadingBookings}
            variant='contained'
            type='submit'
          >
            Создать
          </LoadingButton>
        </AdminBalanceContainer>
      </form>
    </FormProvider>
  );
};

export default AdminTrainings;
